import { __awaiter } from "tslib";
import { watch, onMounted, defineComponent, computed, ref, toRef } from 'vue';
import { NodeAlign, ValueAlign } from '@getaccept/editor-lib-new';
import { useContractPusher } from '@getaccept/editor-lib-new/src/components/composables/contract-pusher.composable';
import { useContractLink } from '@getaccept/editor-lib-new/src/components/composables/contract-link.composable';
import { RecipientStatus } from '@getaccept/lib-shared-new/src/recipients/enums/recipient-status';
import ContractLinkHeader from '@getaccept/editor-lib-new/src/components/contract-link/ContractLinkHeader.vue';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { ContractHelper as ContractHelperShared } from '@getaccept/editor-lib-new/src/helpers/contract.helper';
import { RecipientRole } from '@getaccept/lib-shared-new/src/recipients/enums/recipient-role';
import EditorNodeWrapper from '../EditorNodeWrapper.vue';
import { ContractHelper } from '../helpers/contract.helper';
import DescriptionInput from '../DescriptionInput.vue';
import SignerDetails from './SignerDetails.vue';
export default defineComponent({
    name: 'EditorNodeContractLink',
    components: {
        EditorNodeWrapper,
        SignerDetails,
        ContractLinkHeader,
        DescriptionInput,
    },
    props: {
        node: {
            type: Object,
            default: () => ({}),
        },
        dsrTemplateId: { type: String, default: '' },
        mergeValues: { type: Object, required: true },
        parentFirstChild: { type: Boolean },
        parentLastChild: { type: Boolean },
        blockWidthClass: { type: String, default: null },
        rowTypeClass: { type: String, default: null },
        recipient: { type: Object, default: () => ({}) },
        currentUser: { type: Object, default: undefined },
        pusherClient: { type: Object, default: undefined },
        linkedContract: { type: Object, default: () => ({}) },
    },
    emits: ['observe-node-visibility'],
    setup(props) {
        const participantPusherClient = ref(null);
        const pusherClient = computed(() => props.currentUser ? props.pusherClient : participantPusherClient.value);
        const { pusherSubscribe, recipientStatus, documentStatus, recipientRole, newExpirationDate, } = useContractPusher(props, pusherClient);
        const contract = ref(null);
        const signingPageUrl = ref(null);
        const contractSender = computed(() => { var _a; return (_a = contract.value) === null || _a === void 0 ? void 0 : _a.user; });
        const contractLoading = ref(false);
        const senderCompanyName = computed(() => props.mergeValues.entity.companyName || props.mergeValues.entity.name);
        const getSigningPageUrlForRecipient = (id) => {
            const recipientMap = ContractHelper.findMappedRecipient(id, props.recipient, props.node.recipientMaps);
            if (!recipientMap) {
                return null;
            }
            return signingPageUrl.value;
        };
        const loadContract = () => __awaiter(this, void 0, void 0, function* () {
            const { contractId } = props.node;
            if (!contractId) {
                return;
            }
            contract.value = yield getTokenAndLoadContract(contractId);
        });
        const getTokenAndLoadContract = (contractId) => __awaiter(this, void 0, void 0, function* () {
            signingPageUrl.value = yield ContractHelper.getSigningPageUrl(contractId);
            if (!signingPageUrl.value) {
                return null;
            }
            const jwt = yield ContractHelper.getToken(contractId, signingPageUrl.value, props.recipient);
            participantPusherClient.value = yield ContractHelper.createPusherClient(jwt);
            pusherSubscribe();
            return yield ContractHelper.loadContract(jwt);
        });
        const updateRecipientStatus = ({ status, recipientId, }) => __awaiter(this, void 0, void 0, function* () {
            const recipient = contract.value.recipients.find(recipient => recipient.id === recipientId);
            if (!recipient) {
                const { recipients } = yield getTokenAndLoadContract(props.node.contractId);
                contract.value = Object.assign(Object.assign({}, contract.value), { recipients });
                return;
            }
            contract.value = yield ContractHelperShared.updateContractRecipientStatus(contract.value, recipientId, status);
        });
        const loadingContractText = computed(() => t('connecting_contract_to_room', {
            contractName: props.node.name,
        }));
        const showContractDescription = computed(() => { var _a; return Boolean((_a = contractDescription.value.content[0]) === null || _a === void 0 ? void 0 : _a.content); });
        const getPreviousSigner = (index) => {
            for (let i = index - 1; i >= 0; i--) {
                const recipient = filteredRecipients.value[i];
                if ((recipient === null || recipient === void 0 ? void 0 : recipient.role) !== RecipientRole.CC) {
                    return recipient;
                }
            }
        };
        watch(() => props.linkedContract, () => {
            if (props.linkedContract && props.currentUser) {
                contract.value = Object.assign({}, props.linkedContract);
                pusherSubscribe();
            }
        }, { immediate: true });
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            if (props.currentUser) {
                return;
            }
            contractLoading.value = true;
            yield loadContract();
            contractLoading.value = false;
        }));
        const { updateDocumentStatus, updateRecipientRole, filteredRecipients, translatedRole, contractStatus, showContractDetails, contractExpired, contractDescription, } = useContractLink(contract, toRef(props, 'node'));
        watch(recipientStatus, () => updateRecipientStatus(recipientStatus.value));
        watch(documentStatus, () => updateDocumentStatus(documentStatus.value));
        watch(recipientRole, () => updateRecipientRole(recipientRole.value));
        watch(newExpirationDate, () => loadContract());
        return {
            contract,
            contractDescription,
            contractExpired,
            contractLoading,
            senderCompanyName,
            contractSender,
            contractStatus,
            filteredRecipients,
            getPreviousSigner,
            getSigningPageUrlForRecipient,
            updateRecipientStatus,
            loadingContractText,
            NodeAlign,
            showContractDescription,
            showContractDetails,
            signingPageUrl,
            translatedRole,
            getTokenAndLoadContract,
            loadContract,
            ValueAlign,
            RecipientStatus,
        };
    },
});
